import { fetchFaqs, fetchReviews } from "~/fc/Dal/UtilityRepo";
import { fetchPlans } from "~/fc/Dal/PaymentRepo";
import { formatNumberWithCommas } from '~/fc/Helpers/NumericHelpers';
import { getUTCStandardDatetime } from "./Helpers/dateFormatter";
import { LOCALIZED_PRICING_ROUTES_FOR_COUNTRIES } from '~/config/subscription.config'

/**
 * Dsiplays the price string with the currency
 * @param {*} priceText 
 * @param {*} currency 
 * @returns string | price string
 */
export const priceWithCurrency = (priceText, currency) => {
    const price = formatNumberWithCommas(priceText)
    if (!currency) return '$' + price
    return (currency.alignment === 'left' && currency.currencySymbol || '') + price + (currency.alignment === 'right' && currency.currencySymbol || '')
}

/**
 * Formatting the Plans into groups to be segmented in the UI.
 * This is how we display all the custom plans under the same 
 * card.
 * @param {*} plans 
 */
export const formatPlansForPricingPageDisplay = (plans) => {
    const unifiedPlans = [];
    let unifiedAnnualPlans = [];
    const group = {};
    plans?.forEach((plan) => {
        if (plan.freePlan) return
        if (plan.group.includes('custom')) {
            if (!group['custom']) group['custom'] = []
            group['custom'].push(plan)
        } else if (group[plan.group]) {
            group[plan.group].push(plan);
        } else {
            group[plan.group] = [plan];
            if (plan.group.includes('annual')) unifiedAnnualPlans.push(group[plan.group])
            else unifiedPlans.push(group[plan.group])
        }
    });
    unifiedAnnualPlans.push(group['custom'])
    unifiedPlans.push(group['custom']);
    unifiedAnnualPlans = unifiedAnnualPlans.map(planGroup => {
        if (planGroup?.length === 1) {
            const monthlyPlan = plans.find(item => (planGroup[0].name === item.name) && !item.group.includes('annual'))
            planGroup[0]['monthlyPrice'] = monthlyPlan?.price?.amount || null
        }
        return planGroup
    })

    return [unifiedAnnualPlans, unifiedPlans]
}

/**
 * Sorts fetched FAQ items based on the order property
 * @param {*} faqs 
 * @returns 
 */
export const sortFAQs = (faqs) => {
    return faqs.sort((a, b) => { return a.order > b.order ? 1 : -1 });
}

/**
 * Gettin the data needed to render the page. FAQs, Reviews
 * and the Plan details are fetched.
 * @param {*} app | $nuxt app
 * @returns 
 */
export const getPricingPageData = async (app, store) => {
    const params = { type: "pricing" };
    if (app.i18n && app.i18n.locale) params.lang = app.i18n.locale;
    const headers = { 'User-Agent': `freeconvert-system-${process.env.NODE_ENV}-frontend` }
    if (store.state.store.country && store.state.store.country !== 'US') {
        headers['fc-country-code'] = store.state.store.country
    }
    let [{ data: faqs }, { data: plans }, { data: reviews }] = await Promise.all([fetchFaqs(app, params, headers), fetchPlans(params, headers), fetchReviews(app)]);
    faqs = faqs ? sortFAQs(faqs) : []
    return [faqs, plans, reviews]
}

/**
 * defines and checks some plan related data to be 
 * used on the checkout page
 * @param {*} redirect 
 * @returns 
 */
export const initiatePlanForCheckoutPage = async (redirect) => {
    let formLoading = true
    const handleError = () => { redirect(); return [{}, null, null, null]; }

    const plan = JSON.parse(localStorage.getItem('selectedPlan') || 'null');

    /**
     * We are redirecting the user to the pricing if the plan 
     * is not there in the localStorage or if the plan does not 
     * have the price -> amount property.
     */
    if (!plan || !plan.price.amount) return handleError()
    formLoading = false
    const amount = parseFloat(plan.price.amount)
    const planName = plan.name + plan.type === 'unified-subscription' ? ' Plan' : ' Credit'
    
    return [plan, amount, planName, formLoading]
}


/**
 * defines and checks some subscription related data to be 
 * used on the checkout page
 * @param {*} plan 
 * @param {*} callback 
 * @returns 
 */
export const initiateSubscriptionForCheckoutPage = async (plan, callback) => {
    let [currentSubscriptions, currentPeriodEnd, annualPlanEndDate, isAnnualToMonthlySwitch] = [{}, null, null, false]
    if (localStorage.getItem("currentSubscriptions")) currentSubscriptions = await JSON.parse(localStorage.getItem("currentSubscriptions"));    

    if (!Object.keys(currentSubscriptions).length) {
        return [currentSubscriptions, currentPeriodEnd, annualPlanEndDate, isAnnualToMonthlySwitch]
    }

    const unifiedSubscription = currentSubscriptions["unified-subscription"];
    
    if (
        unifiedSubscription?.plan?.group.includes("annual") &&
        plan.type === "unified-subscription" &&
        !plan.group.includes("annual")
    ) {
        currentPeriodEnd = unifiedSubscription.currentPeriodEnd;
        if (currentPeriodEnd) {
            annualPlanEndDate = getUTCStandardDatetime(new Date(currentPeriodEnd));
        }
        isAnnualToMonthlySwitch = true;
    }

    setTimeout(() => {callback()}, 1000)

    return [
        currentSubscriptions,
        currentPeriodEnd,
        annualPlanEndDate,
        isAnnualToMonthlySwitch,
    ];
}

/**
 * Adjusts pricing plans based on the user's country and locale.
 * 
 * - Skips adjustment if the user's country is not localized or plans are already non-USD.
 * - Fetches localized pricing plans and updates the store accordingly.
 * 
 * @param {Object} ctx - Nuxt context object.
 * @returns {Promise<void>} Resolves when pricing plans are adjusted.
 */
export async function adjustPricingPlansForLocale(ctx) {
    try {
        const country = ctx.$store.state.store.country;
        if (!country || !LOCALIZED_PRICING_ROUTES_FOR_COUNTRIES[country]) return;
        if (ctx.unifiedPlans?.[0]?.[0]?.currency?.currencyCode !== 'USD') return;

        const headers = {
            'User-Agent': `freeconvert-system-${process.env.NODE_ENV}-frontend`,
            'fc-country-code': country
        };
        const { data: plans } = await fetchPlans({ lang: ctx.$i18n.locale }, headers);
        const groups = formatPlansForPricingPageDisplay(plans);

        if (groups?.length) [ctx.unifiedAnnualPlans, ctx.unifiedPlans] = groups;
    } catch (error) {
        console.error('Failed to adjust pricing plans:', error);
    }
}